import axios from 'axios'
import type { FieldValues, Path, UseFormClearErrors, UseFormSetError } from 'react-hook-form'

/**
 * Corpo de erro padronizado devolvido pelo backend
 * (`GlobalExceptionHandler`). Num 422 de validação (`@Valid`), `fields`
 * traz uma mensagem por cada campo inválido — ex:
 * `{ nome: "não deve estar em branco", totalUnidades: "deve ser positivo" }`.
 */
export interface BackendErrorBody {
    code?: string
    message?: string
    fields?: Record<string, string>
}

export function getBackendError(error: unknown): BackendErrorBody | null {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data
        if (data && typeof data === 'object') return data as BackendErrorBody
    }
    return null
}

/** `true` se o pedido foi recusado pelo backend por falta de permissão ou de módulo (403). */
export function isForbiddenError(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 403
}

/** Mapa de erros por campo de um 422 de validação, ou `null` se não houver. */
export function getFieldErrors(error: unknown): Record<string, string> | null {
    const body = getBackendError(error)
    if (body?.fields && Object.keys(body.fields).length > 0) return body.fields
    return null
}

/** Frase legível a partir do mapa de erros por campo, para um toast de recurso. */
export function formatFieldErrors(fields: Record<string, string>): string {
    return Object.entries(fields)
        .map(([field, message]) => `${field}: ${message}`)
        .join(' · ')
}

// ── Registo do formulário activo ────────────────────────────────────────────
//
// O intercetor global de respostas (ver NotificationContext) não tem acesso ao
// `setError` de cada formulário react-hook-form. Cada formulário montado regista
// aqui o seu handler através do hook `useServerFieldErrors`; ao receber um 422
// de validação, o intercetor aplica os erros campo-a-campo ao formulário no topo
// da pilha — tipicamente o diálogo/página de formulário aberto nesse momento.

type FormErrorHandler = {
    setError: UseFormSetError<FieldValues>
    clearErrors: UseFormClearErrors<FieldValues>
    /** Traduz nomes de campo do backend para nomes do formulário quando diferem. */
    fieldMap?: Record<string, string>
}

const handlerStack: FormErrorHandler[] = []

export function pushFormErrorHandler(handler: FormErrorHandler): () => void {
    handlerStack.push(handler)
    return () => {
        const index = handlerStack.lastIndexOf(handler)
        if (index !== -1) handlerStack.splice(index, 1)
    }
}

/**
 * Aplica `fields` (mapa campo → mensagem) ao formulário activo. Devolve `true`
 * se havia um formulário para receber os erros — nesse caso o chamador deve
 * dispensar o toast genérico de gravação.
 */
export function applyFieldErrorsToActiveForm(fields: Record<string, string>): boolean {
    const handler = handlerStack[handlerStack.length - 1]
    if (!handler) return false

    // Limpa erros de servidor anteriores para que uma nova tentativa não fique
    // bloqueada por um erro de um campo que já foi corrigido.
    handler.clearErrors()

    let first = true
    for (const [backendField, message] of Object.entries(fields)) {
        const name = (handler.fieldMap?.[backendField] ?? backendField) as Path<FieldValues>
        handler.setError(name, { type: 'server', message }, { shouldFocus: first })
        first = false
    }
    return true
}
